import { useState, useRef, useEffect } from 'react';

/**
 * Defines an effect that appears when a new column is added, and does two things in sequence:
 *  - Scrolls to reveal the new column
 *  - Indicates the new column
 *
 * The hook handles the first part, but leaves the second part for the consuming component to
 * handle. However, it returns a state variable (indicateNewColumn) to communicate when it's
 * time to indicate the column.
 */
export function useNewColumnIndication(
  columnCount: number,
  scrollableEl?: HTMLDivElement | null
) {
  const [indicateNewColumn, setIndicateNewColumn] = useState(-1);
  const previousColumnCount = useRef(columnCount);
  const timeout = useRef<any>(null);
  useEffect(() => {
    if (columnCount > previousColumnCount.current) {
      clearTimeout(timeout.current);
      const el = scrollableEl ?? document.documentElement;
      el.scrollTo({
        left: el.scrollWidth - el.clientWidth,
        behavior: 'smooth',
      });
      timeout.current = setTimeout(() => {
        setIndicateNewColumn(columnCount - 1);
      }, 300);
    }
    previousColumnCount.current = columnCount;
  }, [columnCount, scrollableEl]);

  return {
    indicateNewColumn,
  };
}
